import { NgTemplateOutlet } from '@angular/common';
import type { ElementRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  linkedSignal,
  type OnDestroy,
  type OnInit,
  output,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import type { OdlPopover } from '@odilo/ui-components';
import ResizeObserver from 'resize-observer-polyfill';
import { isHighlight } from '../../../helpers/selections';
import type { QuizMaterialResponse } from '../../../models/quiz';
import type { HighlightColor } from '../../../models/selections';
import { FeatureFlagsService } from '../../../services/feature-flags/feature-flags';
import { OfflineSessionService } from '../../../services/offline/offline-session';
import { MetadataService } from '../../../services/metadata/metadata';
import { QuizService } from '../../../services/quiz/quiz';
import { ReaderNavigatorService } from '../../../services/readium/navigator/reader-navigator';
import { HighlightsService } from '../../../services/selections/highlights';
import { SelectionsService } from '../../../services/selections/selections';
import { TextInfoService } from '../../../services/text-info/text-info';
import { TextSearchService } from '../../../services/text-search/text-search';
import { AnnotationColorButtonComponent } from '../annotation-color-button/annotation-color-button';

@Component({
  selector: 'nr-text-selection-popover',
  imports: [AnnotationColorButtonComponent, TranslatePipe, NgTemplateOutlet],
  templateUrl: './text-selection-popover.html',
  styleUrl: './text-selection-popover.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TextSelectionPopoverComponent implements OnInit, OnDestroy {
  private readonly readerNavigatorService = inject(ReaderNavigatorService);
  private readonly textSearchService = inject(TextSearchService);
  private readonly textInfoService = inject(TextInfoService);
  private readonly metadataService = inject(MetadataService);
  private readonly selectionsService = inject(SelectionsService);
  private readonly highlightsService = inject(HighlightsService);
  private readonly quizService = inject(QuizService);
  private readonly popover = viewChild.required<ElementRef<OdlPopover>>('popover');
  private resizeObserver?: ResizeObserver;
  private reopening = false;

  readonly featureFlags = inject(FeatureFlagsService);
  readonly colors = this.highlightsService.colors;
  readonly colorMap = this.highlightsService.colorMap;
  readonly colorLabels = this.highlightsService.colorLabelMap;

  readonly position = input<DOMRect | undefined>();
  readonly nrClose = output();

  readonly isTeacher = this.metadataService.isTeacher;
  readonly canUseAi = this.metadataService.canUseAi;
  readonly canCreateVoiceQuestions = this.metadataService.canCreateVoiceQuestions;
  readonly showTeacherQuestions = computed<boolean>(
    () => this.metadataService.canManageQuestions() && this.featureFlags.isEnabled('questions')(),
  );
  readonly selectedColor = linkedSignal<HighlightColor | undefined>(
    () => this.readerNavigatorService.selectionEdit()?.color,
  );
  readonly visible = computed<boolean>(() => Boolean(this.position()));
  readonly selectionContext = this.readerNavigatorService.currentSelectionContext;
  readonly selectedText = computed<string | undefined>(() => this.selectionContext()?.selectedText);
  // A highlight opened by clicking it has no live selection: search for its own text then.
  private readonly textToSearch = computed<string>(
    () => this.readerNavigatorService.selectionEdit()?.selectedText ?? this.selectedText() ?? '',
  );
  // The server rejects searches shorter than its minimum, so don't offer one it would refuse.
  readonly canSearch = computed<boolean>(() =>
    this.textSearchService.isSearchable(this.textToSearch()),
  );
  readonly minCharsToSearch = this.textSearchService.minCharsToSearch;
  readonly isUserFromBookClub = this.metadataService.isUserFromBookClub;
  /** Look up, share and send to club need the API. */
  readonly isOffline = inject(OfflineSessionService).isOffline;
  readonly isEditingHighlight = computed<boolean>(() =>
    Boolean(this.readerNavigatorService.selectionEdit()),
  );
  // A highlight opened via a shared link: read-only, can't be recolored, noted, shared again,
  // sent to a club, or deleted — see HighlightsService/SelectionsService for how it's injected.
  readonly isSharedHighlight = computed<boolean>(() =>
    Boolean(this.readerNavigatorService.selectionEdit()?.isShared),
  );
  // On desktop, a shared highlight's "more" menu only keeps Look up, and only when that isn't
  // already in the toolbar (teachers get it in the menu): otherwise the menu would open empty.
  readonly hasDesktopMoreActions = computed<boolean>(
    () => !this.isSharedHighlight() || this.showTeacherQuestions(),
  );
  // A shared highlight has nothing to show behind the note button unless it came with a note.
  readonly canOpenNote = computed<boolean>(() => {
    const editingHighlight = this.readerNavigatorService.selectionEdit();
    return !editingHighlight?.isShared || Boolean(editingHighlight.notes);
  });
  // Swatch shown collapsed in the toolbar: the color in use, or the default one
  // before any color has been picked for this selection.
  readonly currentColor = computed<HighlightColor>(() => this.selectedColor() ?? this.colors[0]);
  readonly existingMaterialForSelection = computed<QuizMaterialResponse | undefined>(() => {
    const highlight = this.readerNavigatorService.selectionEdit();
    if (!highlight) return undefined;
    const legacyCfi = 'locationCfi' in highlight ? highlight.locationCfi : undefined;
    return this.quizService
      .quizzes()
      .map(({ quiz }) => quiz)
      .find(
        (quiz) =>
          quiz.selection.id === highlight.id ||
          (legacyCfi !== undefined && quiz.selection.percent === legacyCfi),
      );
  });

  constructor() {
    effect(() => {
      const position = this.position();
      const wasOpen = this.popover().nativeElement.open;
      this.reopening = Boolean(position);
      this.popover().nativeElement.open = false;
      if (!position) return;

      const reopen = (): void => {
        this.popover().nativeElement.for = position;
        this.popover().nativeElement.open = true;
        this.reopening = false;
      };

      (wasOpen ? this.waitForNativelyClosed() : this.popover().nativeElement.updateComplete).then(
        reopen,
      );
    });
  }

  private async waitForNativelyClosed(): Promise<void> {
    const container =
      this.popover().nativeElement.shadowRoot?.querySelector<HTMLElement>('#popover');
    if (!container || container.hidden) return Promise.resolve();
    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        if (container.hidden) {
          observer.disconnect();
          resolve();
        }
      });
      observer.observe(container, { attributes: true, attributeFilter: ['hidden'] });
    });
  }

  // The browser's own (blue) selection is painted over the new highlight, hiding its color.
  private clearBrowserSelection(): void {
    this.readerNavigatorService.currentWindowIframe()?.getSelection()?.removeAllRanges();
  }

  onPopoverClosed(): void {
    if (this.reopening) return;
    this.readerNavigatorService.selectionEdit.set(undefined);
    this.nrClose.emit();
  }

  ngOnInit(): void {
    this.resizeObserver = new ResizeObserver(() => {
      this.nrClose.emit();
    });
    this.resizeObserver.observe(document.body);
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  async selectColor(color: HighlightColor): Promise<void> {
    const editingHighlight = this.readerNavigatorService.selectionEdit();
    if (editingHighlight?.isShared) return;

    if (editingHighlight) {
      if (editingHighlight.color === color) {
        this.highlightsService.removeHighlight(editingHighlight);
        await this.selectionsService.removeSelection(editingHighlight.id);
        this.readerNavigatorService.selectionEdit.set(undefined);
        this.nrClose.emit();
        return;
      }

      this.selectedColor.set(color);
      await this.selectionsService.updateSelection(editingHighlight.id, {
        ...editingHighlight,
        color,
      });
      this.highlightsService.removeHighlight(editingHighlight);
      this.highlightsService.loadVisibleHighlights();
      this.nrClose.emit();
      return;
    }

    if (!this.selectedText()) return;

    this.selectedColor.set(color);

    const selectedText = this.selectedText();

    await this.selectionsService.saveSelection({
      selectedText,
      color,
      isBookmark: false,
    });

    this.clearBrowserSelection();
    this.nrClose.emit();
  }

  async createNote(): Promise<void> {
    const editingHighlight = this.readerNavigatorService.selectionEdit();

    if (editingHighlight) {
      this.highlightsService.noteIsDraft.set(false);
      this.highlightsService.noteToEdit.set(editingHighlight);
      this.nrClose.emit();
      return;
    }

    if (!this.selectedText()) return;

    const selection = await this.selectionsService.saveSelection({
      selectedText: this.selectedText(),
      color: 'highlight-yellow',
      isBookmark: false,
    });
    if (selection && isHighlight(selection)) {
      this.highlightsService.noteToEdit.set(selection);
      this.highlightsService.noteIsDraft.set(true);
      this.readerNavigatorService.selectionEdit.set(selection);
      this.clearBrowserSelection();
      this.nrClose.emit();
    }
  }

  openTextInfo(): void {
    const editingHighlight = this.readerNavigatorService.selectionEdit();
    this.textInfoService.selectedText.set(
      editingHighlight?.selectedText ?? this.selectedText() ?? '',
    );
    this.nrClose.emit();
  }

  async sendToClub(): Promise<void> {
    const editingHighlight = this.readerNavigatorService.selectionEdit();
    if (editingHighlight?.isShared) return;

    if (editingHighlight) {
      this.nrClose.emit();
      await this.selectionsService.sendClubComment(editingHighlight);
      return;
    }

    if (!this.selectedText()) return;

    const selection = await this.selectionsService.saveSelection({
      selectedText: this.selectedText(),
      color: 'highlight-yellow',
      isBookmark: false,
    });
    if (selection && isHighlight(selection)) {
      this.clearBrowserSelection();
      this.nrClose.emit();
      await this.selectionsService.sendClubComment(selection);
    }
  }

  async createQuestion(ai: boolean): Promise<void> {
    if (ai && !this.canUseAi()) return;
    await this.quizService.startCreateQuizFromSelection(ai, this.selectedText(), 'question');
    this.nrClose.emit();
  }

  async createQuiz(ai: boolean): Promise<void> {
    if (ai && !this.canUseAi()) return;
    await this.quizService.startCreateQuizFromSelection(ai, this.selectedText());
    this.nrClose.emit();
  }

  async createPublicNote(): Promise<void> {
    await this.quizService.startCreateQuizFromSelection(false, this.selectedText(), 'note');
    this.nrClose.emit();
  }

  async createVoiceRecording(): Promise<void> {
    await this.quizService.startCreateQuizFromSelection(false, this.selectedText(), 'voice');
    this.nrClose.emit();
  }

  openExistingMaterial(): void {
    const quiz = this.existingMaterialForSelection();
    if (!quiz) return;
    if (this.isTeacher() || quiz.materialKind === 'annotation') {
      this.quizService.startEditQuiz(quiz);
    } else {
      this.quizService.startAnswerQuiz(quiz);
    }
    this.nrClose.emit();
  }

  search(): void {
    if (!this.canSearch()) return;
    this.nrClose.emit();
    this.textSearchService.search(this.textToSearch());
  }

  async share(): Promise<void> {
    const editingHighlight = this.readerNavigatorService.selectionEdit();
    if (editingHighlight?.isShared) return;

    if (editingHighlight) {
      this.nrClose.emit();
      await this.highlightsService.requestShare(editingHighlight);
      return;
    }

    if (!this.selectedText()) return;

    const selection = await this.selectionsService.saveSelection({
      selectedText: this.selectedText(),
      color: 'highlight-yellow',
      isBookmark: false,
    });
    if (selection && isHighlight(selection)) {
      this.readerNavigatorService.selectionEdit.set(selection);
      this.nrClose.emit();
      await this.highlightsService.requestShare(selection);
    }
  }

  async delete(): Promise<void> {
    const editingHighlight = this.readerNavigatorService.selectionEdit();
    if (!editingHighlight || editingHighlight.isShared) return;

    this.nrClose.emit();
    this.highlightsService.removeHighlight(editingHighlight);
    await this.selectionsService.removeSelection(editingHighlight.id);
  }
}
